import React, { useState } from 'react';
import { useTenant } from '../Context/TenantContext';
import { useNavigate } from 'react-router-dom';
import { isCollegeChallengeEnabled } from '../utils/collegeChallengeAccess';
import CollegeHeader from './CollegeHeader';
import {
  Users, BarChart3, BookOpen, Rocket, Code2, Trophy,
  Medal, Award, Brain, Target, TrendingUp, ShieldCheck,
  Cpu, Globe, Zap, Star, CheckCircle2, ArrowRight,
  GraduationCap, Building2, Layers, Activity, FileText,
  ChevronRight, Sparkles, Lock, Server, Eye, BadgeCheck,
  Network, GitBranch, Workflow, Smartphone, ChevronDown, ChevronUp,
  Bot, Database, Monitor, Cloud, Wand2, Package, Settings2, Sliders,
} from 'lucide-react';

/* ── Data ─────────────────────────────────────────────────── */

const PERFORMANCE_CARDS = [
  {
    icon: <Users size={22} />,
    title: 'Student Intelligence',
    description: 'Track registrations, active learners, learning activity, enrolled technologies, and engagement metrics across departments.',
    gradient: 'from-blue-500 to-indigo-600',
    bg: 'from-blue-50 to-indigo-50',
    border: 'border-blue-100',
    tag: 'Live Monitoring',
  },
  {
    icon: <BarChart3 size={22} />,
    title: 'MCQ Analytics',
    description: 'Monitor topic-wise practice statistics, completion rates, weak areas, and performance trends with real-time dashboards.',
    gradient: 'from-violet-500 to-purple-600',
    bg: 'from-violet-50 to-purple-50',
    border: 'border-violet-100',
    tag: 'AI Insights',
  },
  {
    icon: <Rocket size={22} />,
    title: 'Career Launchpad Progress',
    description: 'Track role-based learning paths across AI Engineer, Java Developer, Cloud Architect, Full Stack Developer, Data Engineer, DevOps Engineer, and more.',
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'from-emerald-50 to-teal-50',
    border: 'border-emerald-100',
    tag: 'Career Readiness',
  },
  {
    icon: <Target size={22} />,
    title: 'Placement Readiness',
    description: 'Analyze coding skills, resume quality, AI interview scores, certifications, and overall employability for each student.',
    gradient: 'from-amber-500 to-orange-600',
    bg: 'from-amber-50 to-orange-50',
    border: 'border-amber-100',
    tag: 'Placement AI',
  },
];

const STUDENT_FEATURES = [
  { icon: <BookOpen size={18} />, title: '90+ Technology Learning Tracks', color: '#4F46E5', bg: '#EEF2FF' },
  { icon: <Rocket size={18} />, title: 'AI Career Launchpads', color: '#7C3AED', bg: '#F5F3FF' },
  { icon: <Brain size={18} />, title: 'AI Mock Interviews', color: '#DB2777', bg: '#FDF2F8' },
  { icon: <Code2 size={18} />, title: 'Browser-based Code Compiler', color: '#D97706', bg: '#FFFBEB' },
  { icon: <Target size={18} />, title: 'Coding Challenges', color: '#059669', bg: '#F0FDF4' },
  { icon: <FileText size={18} />, title: 'Resume Evaluator', color: '#0891B2', bg: '#ECFEFF' },
  { icon: <Globe size={18} />, title: 'Technology Documentation', color: '#7C3AED', bg: '#F5F3FF' },
  { icon: <Star size={18} />, title: 'XP & Achievement Badges', color: '#D97706', bg: '#FFFBEB' },
  { icon: <Award size={18} />, title: 'Digital Certificates', color: '#059669', bg: '#F0FDF4' },
  { icon: <Activity size={18} />, title: 'Skill Progress Analytics', color: '#4F46E5', bg: '#EEF2FF' },
  { icon: <TrendingUp size={18} />, title: 'Placement Readiness Tracking', color: '#DB2777', bg: '#FDF2F8' },
  { icon: <Layers size={18} />, title: 'Industry-Aligned Assessments', color: '#0891B2', bg: '#ECFEFF' },
];

const DASHBOARD_ACTIONS = [
  { icon: <Users size={20} />, title: 'Manage Students', desc: 'Bulk upload, enroll & track', color: '#4F46E5', bg: '#EEF2FF' },
  { icon: <BarChart3 size={20} />, title: 'Learning Analytics', desc: 'Real-time skill monitoring', color: '#7C3AED', bg: '#F5F3FF' },
  { icon: <FileText size={20} />, title: 'MCQ Reports', desc: 'Topic-wise performance', color: '#0891B2', bg: '#ECFEFF' },
  { icon: <Rocket size={20} />, title: 'Career Progress', desc: 'Launchpad tracking', color: '#059669', bg: '#F0FDF4' },
  { icon: <Brain size={20} />, title: 'AI Interview Analysis', desc: 'Interview performance AI', color: '#DB2777', bg: '#FDF2F8' },
  { icon: <Trophy size={20} />, title: 'College Challenges', desc: 'AI scholarship competitions', color: '#D97706', bg: '#FFFBEB' },
  { icon: <Medal size={20} />, title: 'Student Certificates', desc: 'Verified credentials', color: '#7C3AED', bg: '#F5F3FF' },
  { icon: <TrendingUp size={20} />, title: 'Placement Readiness', desc: 'Employability scoring', color: '#059669', bg: '#F0FDF4' },
  { icon: <Activity size={20} />, title: 'Learning Activity', desc: 'Engagement heatmaps', color: '#4F46E5', bg: '#EEF2FF' },
  { icon: <Star size={20} />, title: 'Top Performers', desc: 'Identify high achievers', color: '#D97706', bg: '#FFFBEB' },
];

const STRATEGIC_VALUES = [
  'Data-driven placement planning with AI-powered insights',
  'Early identification of high-performing students',
  'Skill-gap analysis across departments and batches',
  'Recruiter-ready student profiling and reporting',
  'Industry-aligned learning ecosystem for all skills',
  'Accreditation-ready measurable learning outcomes',
  'Improved placement success rates year over year',
  'Institution-wide academic intelligence at a glance',
  'Centralized performance monitoring and dashboards',
  'AI-powered student performance insights in real-time',
];

const TRUST_BADGES = [
  { icon: <Lock size={16} />, label: 'Secure Cloud Platform' },
  { icon: <Cpu size={16} />, label: 'AI-Powered Analytics' },
  { icon: <ShieldCheck size={16} />, label: 'Data Isolation Per Institution' },
  { icon: <Server size={16} />, label: 'Enterprise Grade Security' },
  { icon: <Activity size={16} />, label: 'Real-time Reporting' },
  { icon: <Globe size={16} />, label: 'Multi-device Compatibility' },
  { icon: <Eye size={16} />, label: 'Role-based Access Control' },
  { icon: <Zap size={16} />, label: 'High Availability' },
  { icon: <Layers size={16} />, label: 'Scalable Architecture' },
  { icon: <BadgeCheck size={16} />, label: 'Centralized Monitoring' },
];

const DIFFERENTIATOR_FEATURES = [
  {
    icon: <Rocket size={20} />,
    title: 'Industry-First Curriculum',
    description: 'Our learning roadmap continuously evolves with the latest technologies transforming the software industry, ensuring students stay aligned with current market demands rather than outdated syllabi.',
    accent: 'linear-gradient(135deg, #4F46E5, #6366F1)',
  },
  {
    icon: <FileText size={20} />,
    title: 'Resume That Gets Noticed',
    description: 'Students build portfolios and resumes with practical exposure to next-generation tools, helping them stand out during campus placements and recruitment processes.',
    accent: 'linear-gradient(135deg, #7C3AED, #A855F7)',
  },
  {
    icon: <Code2 size={20} />,
    title: 'Learn Through Real-Time Projects',
    description: 'Every module is built around practical implementation, enterprise-inspired scenarios, AI-assisted workflows, and hands-on exercises that simulate real workplace environments.',
    accent: 'linear-gradient(135deg, #0891B2, #06B6D4)',
  },
  {
    icon: <TrendingUp size={20} />,
    title: 'Career-Ready Skill Development',
    description: 'By mastering emerging technologies, students develop future-ready capabilities that improve confidence, interview readiness, and long-term career growth opportunities.',
    accent: 'linear-gradient(135deg, #059669, #10B981)',
  },
];

const FEATURED_TECHNOLOGIES = [
  {
    icon: <Network size={22} />,
    name: 'Model Context Protocol (MCP)',
    description: 'Learn how modern AI applications securely connect with APIs, enterprise systems, databases, and external tools using standardized context protocols.',
    color: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
  },
  {
    icon: <GitBranch size={22} />,
    name: 'LangGraph',
    description: 'Build intelligent stateful AI agents capable of reasoning, memory management, workflow orchestration, and autonomous execution.',
    color: 'linear-gradient(135deg, #059669, #0891B2)',
  },
  {
    icon: <Workflow size={22} />,
    name: 'n8n (Self-Hosted)',
    description: 'Create enterprise automation workflows, AI pipelines, and business integrations through powerful visual orchestration.',
    color: 'linear-gradient(135deg, #D97706, #F59E0B)',
  },
  {
    icon: <Smartphone size={22} />,
    name: 'FlutterFlow',
    description: 'Design and build production-ready mobile and web applications with AI integration, backend connectivity, and rapid development capabilities.',
    color: 'linear-gradient(135deg, #DB2777, #EC4899)',
  },
];

const MORE_TECHNOLOGIES = [
  { name: 'Mastra & CrewAI',                   icon: <Bot size={14} />,      color: '#4F46E5', bg: '#EEF2FF' },
  { name: 'Ollama & LM Studio',                 icon: <Cpu size={14} />,      color: '#7C3AED', bg: '#F5F3FF' },
  { name: 'Xano & Supabase',                    icon: <Database size={14} />, color: '#0891B2', bg: '#ECFEFF' },
  { name: 'WeWeb & Appsmith',                   icon: <Monitor size={14} />,  color: '#059669', bg: '#F0FDF4' },
  { name: 'Coolify',                            icon: <Cloud size={14} />,    color: '#6366F1', bg: '#EEF2FF' },
  { name: 'v0.dev & Generative UI',             icon: <Wand2 size={14} />,   color: '#8B5CF6', bg: '#F5F3FF' },
  { name: 'Make.com',                           icon: <Sliders size={14} />,  color: '#D97706', bg: '#FFFBEB' },
  { name: 'Katalon Studio',                     icon: <Package size={14} />,  color: '#0891B2', bg: '#ECFEFF' },
  { name: 'Lovable.dev',                        icon: <Star size={14} />,     color: '#DB2777', bg: '#FDF2F8' },
  { name: 'Bolt.new',                           icon: <Zap size={14} />,      color: '#D97706', bg: '#FFFBEB' },
  { name: 'dbt (Data Build Tool)',              icon: <BarChart3 size={14} />,color: '#059669', bg: '#F0FDF4' },
  { name: 'UiPath Studio Orchestrator',         icon: <Settings2 size={14} />,color: '#4F46E5', bg: '#EEF2FF' },
  { name: 'UiPath Studio Web',                  icon: <Globe size={14} />,    color: '#7C3AED', bg: '#F5F3FF' },
  { name: 'UiPath Communications Mining',       icon: <Brain size={14} />,    color: '#DB2777', bg: '#FDF2F8' },
  { name: 'UiPath Document Understanding',      icon: <FileText size={14} />, color: '#0891B2', bg: '#ECFEFF' },
  { name: 'Bubble',                             icon: <Layers size={14} />,   color: '#059669', bg: '#F0FDF4' },
  { name: 'Retool',                             icon: <Code2 size={14} />,    color: '#D97706', bg: '#FFFBEB' },
];

/* ── Main Component ───────────────────────────────────────── */

const CollegeInfoPage = () => {
  const { getCollegeName } = useTenant();
  const navigate = useNavigate();

  const [showMoreTech, setShowMoreTech] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const username = user?.username || user?.userId;
  const collegeName = getCollegeName() || 'Your Institution';

  // College Challenge eligibility — hide all related widgets for ineligible colleges
  const collegeChallengeEnabled = isCollegeChallengeEnabled();
  const visibleDashboardActions = collegeChallengeEnabled
    ? DASHBOARD_ACTIONS
    : DASHBOARD_ACTIONS.filter(action => action.title !== 'College Challenges');

  const handleGoToDashboard = () => {
    navigate(username ? `/collegehome/${username}` : '/');
  };

  return (
    <div className="flex flex-col min-h-screen">
      <CollegeHeader />

        <main className="flex-1 overflow-y-auto">
          {/* ── Hero ───────────────────────────────────────────── */}
          <section className="relative overflow-hidden">
            {/* Background gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-900 to-blue-900 pointer-events-none" />
            <div className="absolute top-0 left-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-indigo-400/10 rounded-full blur-2xl pointer-events-none -translate-x-1/2 -translate-y-1/2" />

            <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 py-16 text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 border border-white/20 bg-white/10 text-white/90 backdrop-blur-sm">
                <GraduationCap size={13} />
                AI-Powered College Intelligence Platform
              </div>

              <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight mb-3">
                Welcome to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
                  {collegeName}
                </span>
              </h1>
              <p className="text-indigo-200 font-semibold text-lg mb-6">
                AI-Powered Student Skill Intelligence &amp; Placement Readiness Platform
              </p>
              <p className="text-indigo-300 text-sm leading-relaxed max-w-2xl mx-auto mb-10">
                Gain complete visibility into your students' learning journey, monitor skill development across technologies,
                identify placement-ready candidates, and empower your institution with actionable AI-driven insights through
                The Skill Genie ecosystem.
              </p>

              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={handleGoToDashboard}
                  className="inline-flex items-center gap-2 bg-white text-indigo-700 font-black px-7 py-3 rounded-xl hover:bg-indigo-50 transition-all active:scale-95 shadow-2xl shadow-black/20 text-sm"
                >
                  Open Dashboard <ArrowRight size={16} />
                </button>
                <a
                  href="#capabilities"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/25 text-white font-bold px-7 py-3 rounded-xl hover:bg-white/20 transition-all text-sm backdrop-blur-sm"
                >
                  Explore Platform <ChevronRight size={16} />
                </a>
              </div>

              {/* Floating stat pills */}
              <div className="flex flex-wrap gap-3 justify-center mt-10">
                {['90+ Tech Tracks', 'AI Interviews', 'Live Analytics', 'Placement AI', 'Digital Certs'].map(label => (
                  <span key={label} className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-white/70 text-xs font-semibold backdrop-blur-sm">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              Future Technologies & Industry Innovation Section
          ══════════════════════════════════════════════════════ */}
          <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50">
            {/* Ambient background glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.06),transparent_65%)] pointer-events-none" />
            <div className="absolute top-20 right-0 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-40 left-0 w-64 h-64 bg-indigo-400/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-16 pb-4">

              {/* ── Section Header ─────────────────────────────── */}
              <div className="text-center mb-12">
                {/* Gold gradient badge */}
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-amber-300/40"
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)',
                    color: 'white',
                    boxShadow: '0 0 24px rgba(245,158,11,0.35)',
                  }}
                >
                  <Sparkles size={12} /> Next-Generation Learning Ecosystem
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight mb-4 leading-tight">
                  Learn the Technologies{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                    Defining the Future
                  </span>
                </h2>
                <p className="text-gray-500 text-sm sm:text-base max-w-3xl mx-auto leading-relaxed">
                  Move beyond traditional learning and empower students with the latest AI, Automation, Agentic AI,
                  Low-Code, and Enterprise Development technologies through real-world implementation and hands-on experience.
                </p>
              </div>

              {/* ── Exclusive Partner Notice ────────────────────── */}
              <div
                className="mb-12 rounded-2xl p-6 sm:p-8 border border-indigo-200/60 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(238,242,255,0.95) 0%, rgba(245,243,255,0.95) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                }}
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-400/8 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-400/8 rounded-full blur-2xl pointer-events-none" />
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Star size={20} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 mb-1.5 text-base">
                      Early Access for Selected Partner Colleges
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      This advanced technology learning initiative will initially be available only for a limited number of partner colleges as part of{' '}
                      <span className="font-bold text-indigo-700">The Skill Genie Premium Institutional Program</span>.
                      Selected institutions will gain early access to exclusive learning modules designed around emerging technologies
                      that are rapidly being adopted by modern enterprises and AI-driven organizations.
                    </p>
                  </div>
                </div>
              </div>

              {/* ── What Makes TSG Different ─────────────────────── */}
              <div className="mb-14">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-2 bg-purple-50 text-purple-600 border border-purple-100">
                    <Star size={11} /> What Makes The Skill Genie Different?
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {DIFFERENTIATOR_FEATURES.map((feat) => (
                    <div
                      key={feat.title}
                      className="flex flex-col p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
                    >
                      {/* Top accent bar */}
                      <div className="absolute top-0 left-0 w-full h-1 rounded-t-2xl" style={{ background: feat.accent }} />
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white flex-shrink-0 shadow-md group-hover:scale-110 transition-transform"
                        style={{ background: feat.accent }}
                      >
                        {feat.icon}
                      </div>
                      <h3 className="font-black text-gray-900 text-sm mb-2 leading-snug">{feat.title}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed flex-1">{feat.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Featured Coming Soon Technologies ────────────── */}
              <div className="mb-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-amber-50 text-amber-600 border border-amber-100">
                    <Zap size={11} /> Featured Technologies
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                    Coming Soon to The Skill Genie
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {FEATURED_TECHNOLOGIES.map((tech) => (
                    <div
                      key={tech.name}
                      className="flex flex-col rounded-2xl border border-gray-100 overflow-hidden bg-white hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group"
                    >
                      {/* Colored top strip */}
                      <div className="h-1.5 w-full" style={{ background: tech.color }} />
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-md group-hover:scale-110 transition-transform"
                            style={{ background: tech.color }}
                          >
                            {tech.icon}
                          </div>
                          {/* Coming Soon badge */}
                          <span
                            className="text-[10px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full text-white flex-shrink-0"
                            style={{
                              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                              boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
                            }}
                          >
                            Coming Soon
                          </span>
                        </div>
                        <h3 className="font-black text-gray-900 text-sm mb-2 leading-snug">{tech.name}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed flex-1">{tech.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Future Technologies – Locked Section ────────── */}
              <div className="pb-6 flex flex-col items-center gap-4">

                {/* Gold exclusive badge */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 14px',
                    borderRadius: '99px',
                    background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
                    border: '1px solid #F59E0B',
                    fontSize: '10px',
                    fontWeight: 800,
                    color: '#92400E',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  🔒 Exclusive Future Roadmap
                </div>

                {/* Locked button with tooltip */}
                <div style={{ position: 'relative', display: 'inline-block' }} className="group">
                  <button
                    disabled
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 28px',
                      borderRadius: '14px',
                      border: '2px solid #7C3AED',
                      background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(79,70,229,0.08))',
                      color: '#5B21B6',
                      fontSize: '14px',
                      fontWeight: 800,
                      cursor: 'not-allowed',
                      letterSpacing: '0.01em',
                      boxShadow: '0 0 0 0 rgba(124,58,237,0)',
                      animation: 'enterpriseGlow 2.5s ease-in-out infinite',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Shimmer overlay */}
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.45) 50%, transparent 65%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmerSlide 2.2s linear infinite',
                        borderRadius: 'inherit',
                        pointerEvents: 'none',
                      }}
                    />
                    🔒
                    <span>More Future Technologies Coming Soon</span>
                  </button>

                  {/* Hover tooltip */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 12px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '300px',
                      background: 'linear-gradient(135deg, #1E1B4B, #312E81)',
                      border: '1px solid rgba(167,139,250,0.3)',
                      borderRadius: '14px',
                      padding: '16px 18px',
                      boxShadow: '0 16px 40px rgba(15,23,42,0.25)',
                      zIndex: 50,
                      opacity: 0,
                      pointerEvents: 'none',
                      transition: 'opacity 180ms ease, transform 180ms ease',
                      textAlign: 'left',
                    }}
                    className="group-hover:opacity-100"
                  >
                    {/* Tooltip arrow */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-6px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '12px', height: '12px',
                      background: '#312E81',
                      border: '1px solid rgba(167,139,250,0.3)',
                      borderTop: 'none', borderLeft: 'none',
                      rotate: '45deg',
                    }} />
                    <p style={{ fontSize: '11px', fontWeight: 800, color: '#C4B5FD', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Exclusive Preview
                    </p>
                    <p style={{ fontSize: '13px', color: '#E0E7FF', margin: 0, lineHeight: 1.65, fontWeight: 400 }}>
                      Additional next-generation technologies are currently under development and will be introduced as part of{' '}
                      <strong style={{ color: '#A5B4FC' }}>The Skill Genie Premium Learning Roadmap</strong>.
                    </p>
                    <p style={{ fontSize: '12px', color: '#818CF8', margin: '10px 0 0', fontWeight: 500 }}>
                      Available first for selected partner colleges.
                    </p>
                  </div>
                </div>

                {/* Muted caption with fade animation */}
                <p
                  style={{
                    fontSize: '12px',
                    color: '#94A3B8',
                    fontWeight: 500,
                    margin: 0,
                    textAlign: 'center',
                    maxWidth: '480px',
                    lineHeight: 1.65,
                    animation: 'captionFade 3s ease-in-out infinite',
                  }}
                >
                  ✨ More cutting-edge AI, Automation, Low-Code, Enterprise, and Data Engineering technologies will be announced soon.
                </p>

                {/* ── Innovation Never Stops – Premium Showcase ─────── */}
                <div
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    maxWidth: '900px',
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 28%, #4C1D95 58%, #1E3A5F 100%)',
                    boxShadow: '0 0 0 1.5px rgba(139,92,246,0.40), 0 24px 64px rgba(79,70,229,0.30), 0 4px 16px rgba(15,23,42,0.20)',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* Animated glow blobs */}
                  <div style={{ position: 'absolute', top: '-60px', left: '-60px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.22) 0%, transparent 70%)', animation: 'innovBlob1 8s ease-in-out infinite', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', bottom: '-40px', right: '-40px', width: '240px', height: '240px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)', animation: 'innovBlob2 10s ease-in-out infinite', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '400px', height: '200px', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(167,139,250,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

                  {/* Mesh grid overlay */}
                  <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                  }} />

                  {/* Floating particles */}
                  {[
                    { top: '15%', left: '8%',  size: 3, delay: '0s',   dur: '6s'  },
                    { top: '60%', left: '12%', size: 2, delay: '1.5s', dur: '8s'  },
                    { top: '25%', right: '9%', size: 4, delay: '0.8s', dur: '7s'  },
                    { top: '70%', right: '7%', size: 2, delay: '2s',   dur: '9s'  },
                    { top: '45%', left: '5%',  size: 2, delay: '3s',   dur: '7.5s'},
                    { top: '82%', right: '15%',size: 3, delay: '1s',   dur: '6.5s'},
                  ].map((p, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        top: p.top, left: p.left, right: p.right,
                        width: p.size, height: p.size,
                        borderRadius: '50%',
                        background: 'rgba(196,181,253,0.70)',
                        animation: `innovFloat ${p.dur} ease-in-out ${p.delay} infinite`,
                        pointerEvents: 'none',
                      }}
                    />
                  ))}

                  {/* Inner content */}
                  <div style={{ position: 'relative', zIndex: 10, padding: '40px 32px 36px' }}>

                    {/* Top row: icon + lab label */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
                      {/* Glowing rocket icon */}
                      <div
                        style={{
                          width: '72px', height: '72px', borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(139,92,246,0.30), rgba(79,70,229,0.20))',
                          border: '1.5px solid rgba(167,139,250,0.45)',
                          boxShadow: '0 0 0 8px rgba(139,92,246,0.08)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '32px', lineHeight: 1,
                          animation: 'innovPulse 6s ease-in-out infinite',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        🚀
                      </div>
                      {/* Lab label */}
                      <span style={{
                        fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
                        textTransform: 'uppercase', color: '#FCD34D',
                        textShadow: '0 0 12px rgba(251,191,36,0.40)',
                      }}>
                        THE SKILL GENIE INNOVATION LAB
                      </span>
                    </div>

                    {/* Main heading */}
                    <h3 style={{ textAlign: 'center', margin: '0 0 14px', fontSize: 'clamp(24px,4vw,38px)', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1, color: '#fff' }}>
                      Innovation{' '}
                      <span style={{ backgroundImage: 'linear-gradient(135deg, #C4B5FD, #A5B4FC, #FCD34D)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        Never Stops.
                      </span>
                    </h3>

                    {/* Subtitle */}
                    <p style={{ textAlign: 'center', color: '#C7D2FE', fontSize: '14px', lineHeight: 1.75, margin: '0 auto 32px', maxWidth: '580px', fontWeight: 400 }}>
                      We continuously identify, evaluate, and integrate emerging technologies that are transforming AI, software engineering, automation, enterprise platforms, and digital innovation.
                    </p>

                    {/* Feature grid – 4 cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
                      {[
                        { emoji: '🤖', color: '#A78BFA', bg: 'rgba(139,92,246,0.14)', border: 'rgba(139,92,246,0.30)', title: 'Emerging AI Technologies',      desc: 'Continuously researching next-generation AI frameworks and intelligent agent ecosystems.' },
                        { emoji: '⚙️', color: '#67E8F9', bg: 'rgba(6,182,212,0.12)',   border: 'rgba(6,182,212,0.28)',   title: 'Enterprise Automation',         desc: 'Preparing students for intelligent business process automation and enterprise workflows.' },
                        { emoji: '💻', color: '#86EFAC', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.28)',   title: 'Modern Dev Platforms',          desc: 'Introducing cutting-edge development platforms that redefine application engineering.' },
                        { emoji: '🚀', color: '#FCD34D', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.28)', title: 'Future Career Readiness',       desc: 'Helping students stay ahead of industry trends with practical exposure to tomorrow\'s technologies.' },
                      ].map((card, i) => (
                        <div
                          key={i}
                          style={{
                            background: card.bg,
                            border: `1px solid ${card.border}`,
                            borderRadius: '16px',
                            padding: '18px 16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            display: 'flex', flexDirection: 'column', gap: '8px',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px rgba(0,0,0,0.25)`; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';   e.currentTarget.style.boxShadow = 'none'; }}
                        >
                          <span style={{ fontSize: '22px', lineHeight: 1 }}>{card.emoji}</span>
                          <p style={{ margin: 0, fontSize: '12px', fontWeight: 800, color: card.color, lineHeight: 1.3 }}>{card.title}</p>
                          <p style={{ margin: 0, fontSize: '11px', color: '#A5B4FC', lineHeight: 1.65, fontWeight: 400 }}>{card.desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* Premium highlight bar – gold accent */}
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: '12px',
                      background: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.08))',
                      border: '1px solid rgba(251,191,36,0.35)',
                      borderRadius: '14px',
                      padding: '14px 18px',
                      marginBottom: '28px',
                      boxShadow: '0 0 20px rgba(251,191,36,0.08)',
                    }}>
                      <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>🔒</span>
                      <div>
                        <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 900, color: '#FCD34D', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          Exclusive Early Access
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#E0E7FF', lineHeight: 1.65, fontWeight: 400 }}>
                          Early access to upcoming technology tracks will be provided exclusively to selected partner colleges as part of our phased innovation rollout.
                        </p>
                      </div>
                    </div>

                    {/* Innovation metrics – 4 stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '28px' }}>
                      {[
                        { value: '15+',       label: 'Future Technologies Planned',  color: '#C4B5FD' },
                        { value: 'AI First',  label: 'Learning Ecosystem',           color: '#67E8F9' },
                        { value: 'Real-Time', label: 'Industry Implementations',     color: '#86EFAC' },
                        { value: 'Exclusive', label: 'Partner College Rollout',      color: '#FCD34D' },
                      ].map((stat, i) => (
                        <div
                          key={i}
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '14px',
                            padding: '16px 12px',
                            textAlign: 'center',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                          }}
                        >
                          <p style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 900, color: stat.color, letterSpacing: '-0.02em' }}>{stat.value}</p>
                          <p style={{ margin: 0, fontSize: '10px', color: '#94A3B8', fontWeight: 600, lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Bottom quote */}
                    <div style={{ textAlign: 'center', padding: '0 8px' }}>
                      <p style={{
                        margin: 0, fontSize: '13px', fontStyle: 'italic', lineHeight: 1.8,
                        color: '#C7D2FE', fontWeight: 500,
                        borderTop: '1px solid rgba(167,139,250,0.20)',
                        paddingTop: '20px',
                      }}>
                        "The technologies students learn today determine the opportunities they unlock tomorrow. Our mission is to ensure they stay ahead of industry evolution—not behind it."
                      </p>
                    </div>

                  </div>
                </div>

              </div>

              <style>{`
                @keyframes enterpriseGlow {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.0), 0 2px 8px rgba(124,58,237,0.10); }
                  50%       { box-shadow: 0 0 0 6px rgba(124,58,237,0.10), 0 4px 16px rgba(124,58,237,0.20); }
                }
                @keyframes shimmerSlide {
                  0%   { background-position: 200% 0; }
                  100% { background-position: -200% 0; }
                }
                @keyframes captionFade {
                  0%, 100% { opacity: 0.55; }
                  50%       { opacity: 1; }
                }
                .group:hover .group-hover\\:opacity-100 { opacity: 1 !important; }

                /* Innovation Lab animations */
                @keyframes innovPulse {
                  0%, 100% { box-shadow: 0 0 0 8px rgba(139,92,246,0.08); }
                  50%       { box-shadow: 0 0 0 18px rgba(139,92,246,0.15), 0 0 40px rgba(139,92,246,0.18); }
                }
                @keyframes innovBlob1 {
                  0%, 100% { transform: translate(0, 0) scale(1); }
                  33%       { transform: translate(30px, -20px) scale(1.08); }
                  66%       { transform: translate(-15px, 25px) scale(0.95); }
                }
                @keyframes innovBlob2 {
                  0%, 100% { transform: translate(0, 0) scale(1); }
                  40%       { transform: translate(-25px, 20px) scale(1.06); }
                  70%       { transform: translate(18px, -18px) scale(0.94); }
                }
                @keyframes innovFloat {
                  0%, 100% { transform: translateY(0px) scale(1); opacity: 0.55; }
                  50%       { transform: translateY(-10px) scale(1.2); opacity: 1; }
                }
              `}</style>

            </div>

            {/* ── Premium Differentiator Banner ────────────────── */}
            <div
              className="relative overflow-hidden mt-16"
              style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 30%, #4C1D95 65%, #1E3A5F 100%)' }}
            >
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,92,246,0.25),transparent_60%)] pointer-events-none" />
              <div className="absolute top-0 left-0 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none -translate-x-1/3 -translate-y-1/3" />
              <div className="absolute bottom-0 right-0 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3" />

              <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-14">
                <div className="flex flex-col lg:flex-row gap-10 items-start">
                  {/* Left: Text content */}
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5 border border-white/20 bg-white/10 text-white/90 backdrop-blur-sm">
                      <Rocket size={12} /> Why The Skill Genie Stands Apart
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-5 leading-tight">
                      Why The Skill Genie{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
                        Stands Apart
                      </span>
                    </h2>
                    <div className="space-y-3">
                      {[
                        'Technology is evolving faster than ever, and organizations increasingly seek professionals who understand AI-native development, intelligent automation, modern application platforms, and enterprise orchestration.',
                        'The Skill Genie is designed to bridge this gap by introducing students to emerging technologies through practical learning, industry-inspired projects, and hands-on implementation experiences.',
                        'Rather than focusing only on theoretical knowledge, our platform emphasizes applied skills that help students create stronger portfolios, enhance their resumes, improve interview confidence, and become better prepared for modern technology careers.',
                        'This forward-looking approach enables institutions to provide their students with a learning experience aligned with the future of the software industry.',
                      ].map((para, i) => (
                        <p key={i} className="text-indigo-200 text-sm leading-relaxed">{para}</p>
                      ))}
                    </div>
                  </div>

                  {/* Right: Program Availability card */}
                  <div className="flex-shrink-0 w-full lg:w-72">
                    <div
                      className="rounded-2xl p-6 border border-white/15 relative overflow-hidden"
                      style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />
                      <div className="flex items-center gap-3 mb-4 relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
                          <Star size={18} className="text-amber-300" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-0.5">Program Availability</p>
                          <h3 className="font-black text-white text-sm leading-snug">Limited Rollout</h3>
                        </div>
                      </div>
                      <p className="text-indigo-200 text-xs leading-relaxed mb-5 relative z-10">
                        The technologies showcased above are part of The Skill Genie's upcoming innovation roadmap.
                        Access will initially be provided only to selected partner colleges through a phased rollout
                        strategy before expanding to additional institutions.
                      </p>
                      <div className="flex flex-wrap gap-2 relative z-10">
                        {['Phased Rollout', 'Partner Colleges', 'Early Access'].map(tag => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 rounded-full text-[10px] font-bold border border-white/15 text-white/70"
                            style={{ background: 'rgba(255,255,255,0.08)' }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Why Colleges Choose The Skill Genie ─────────────── */}
            <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16">

              {/* Badge */}
              <div className="flex justify-center mb-6">
                <span
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border"
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)',
                    borderColor: 'rgba(245,158,11,0.4)',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(245,158,11,0.25)',
                  }}
                >
                  <Trophy size={13} /> THE SKILL GENIE ADVANTAGE
                </span>
              </div>

              {/* Main heading */}
              <h2 className="text-2xl sm:text-4xl font-black text-gray-900 tracking-tight text-center mb-4 leading-tight">
                Why Leading Institutions Choose{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  The Skill Genie
                </span>
              </h2>

              {/* Subtitle */}
              <p className="text-gray-500 text-sm sm:text-base max-w-3xl mx-auto text-center mb-12 leading-relaxed">
                The Skill Genie is designed as a <strong className="text-gray-700">complete AI-powered student skill development and placement readiness ecosystem</strong>, helping institutions prepare students with practical industry experience rather than just theoretical knowledge.
              </p>

              {/* ── Comparison columns ────────────────────────────── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">

                {/* Left — Traditional */}
                <div
                  className="rounded-2xl border border-gray-200 bg-white/60 p-6 sm:p-8"
                  style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                      <Monitor size={16} className="text-gray-400" />
                    </div>
                    <h3 className="font-black text-gray-600 text-base tracking-tight">Traditional Learning Platforms</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Generic technology courses',
                      'Mostly recorded video content',
                      'Limited interview preparation',
                      'No proof of consistent coding practice',
                      'Manual or delayed code feedback',
                      'Focus on theory-based learning',
                      'Generic certificates',
                      'Expensive premium subscriptions',
                      'Separate tools for learning, coding, and assessment',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                        <span className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-gray-300 text-[10px] font-black">✗</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right — The Skill Genie */}
                <div
                  className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(238,242,255,0.95) 0%, rgba(245,240,255,0.95) 100%)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box',
                    boxShadow: '0 0 0 2px rgba(99,102,241,0.35), 0 8px 32px rgba(99,102,241,0.12)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                  }}
                >
                  {/* Glow orbs */}
                  <div className="absolute top-0 right-0 w-48 h-48 bg-purple-400/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-36 h-36 bg-indigo-400/10 rounded-full blur-2xl pointer-events-none" />

                  <div className="relative z-10">
                    {/* Card header */}
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)' }}
                        >
                          <Sparkles size={16} className="text-white" />
                        </div>
                        <h3 className="font-black text-gray-900 text-base tracking-tight">The Skill Genie Platform</h3>
                      </div>
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white"
                        style={{ background: 'linear-gradient(135deg,#F59E0B,#D97706)' }}
                      >
                        <Star size={9} /> Next Generation Learning Platform
                      </span>
                    </div>

                    {/* Advantage rows */}
                    <ul className="space-y-3">
                      {[
                        'Domain-centric learning paths aligned with specific career roles and industries',
                        'Structured Bootcamp programs with guided implementation and practical assignments',
                        'AI-powered Mock Interviews with detailed performance analysis and improvement insights',
                        'Git Commit tracking to showcase real development activity and coding consistency',
                        'AI Agent-powered Code Reviews with instant recommendations and best practices',
                        'Real-world projects, enterprise scenarios, and hands-on implementation experience',
                        'Portfolio-building projects and industry-ready skill validation',
                        'Affordable pricing designed to maximize student accessibility and institutional value',
                        'Unified platform integrating learning, coding, assessments, interviews, certifications, and placement readiness',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                          <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-emerald-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* ── Feature highlight cards ───────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
                {[
                  {
                    emoji: '🎯',
                    title: 'Domain-Centric Learning',
                    desc: 'Students follow structured learning paths designed around real job roles and industry domains instead of disconnected topics.',
                    grad: 'from-indigo-500 to-blue-600',
                    bg: 'from-indigo-50/80 to-blue-50/80',
                    border: 'border-indigo-100',
                  },
                  {
                    emoji: '🚀',
                    title: 'Guided Bootcamp Programs',
                    desc: 'Comprehensive bootcamps with milestones, projects, assessments, and mentor-guided progression for accelerated learning.',
                    grad: 'from-purple-500 to-violet-600',
                    bg: 'from-purple-50/80 to-violet-50/80',
                    border: 'border-purple-100',
                  },
                  {
                    emoji: '🤖',
                    title: 'AI Mock Interviews',
                    desc: 'Practice technical interviews with AI-generated questions, instant evaluation, detailed feedback, and improvement suggestions.',
                    grad: 'from-fuchsia-500 to-pink-600',
                    bg: 'from-fuchsia-50/80 to-pink-50/80',
                    border: 'border-fuchsia-100',
                  },
                  {
                    emoji: '💻',
                    title: 'Git Commit Portfolio',
                    desc: 'Track coding consistency through Git commits and demonstrate continuous practical development experience to recruiters.',
                    grad: 'from-emerald-500 to-teal-600',
                    bg: 'from-emerald-50/80 to-teal-50/80',
                    border: 'border-emerald-100',
                  },
                  {
                    emoji: '🧠',
                    title: 'AI Agent Code Review',
                    desc: 'Receive intelligent code reviews with optimization tips, best practices, security recommendations, and quality improvements in real time.',
                    grad: 'from-orange-500 to-amber-600',
                    bg: 'from-orange-50/80 to-amber-50/80',
                    border: 'border-orange-100',
                  },
                  {
                    emoji: '💰',
                    title: 'Exceptional Value',
                    desc: 'Access an integrated ecosystem of learning, coding, assessments, certifications, and placement preparation at a price point that delivers outstanding institutional value.',
                    grad: 'from-rose-500 to-red-600',
                    bg: 'from-rose-50/80 to-red-50/80',
                    border: 'border-rose-100',
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className={`group rounded-2xl border ${card.border} bg-gradient-to-br ${card.bg} p-6 flex flex-col gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
                    style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                  >
                    <div
                      className={`w-11 h-11 rounded-xl bg-gradient-to-br ${card.grad} flex items-center justify-center text-xl shadow-md group-hover:scale-110 transition-transform duration-300`}
                    >
                      {card.emoji}
                    </div>
                    <h4 className="font-black text-gray-900 text-sm tracking-tight leading-snug">{card.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{card.desc}</p>
                  </div>
                ))}
              </div>

              {/* ── Premium info banner ───────────────────────────── */}
              <div
                className="rounded-2xl p-8 sm:p-10 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(79,70,229,0.08) 0%, rgba(124,58,237,0.10) 50%, rgba(79,70,229,0.06) 100%)',
                  border: '1.5px solid rgba(99,102,241,0.20)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 32px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
                }}
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-400/8 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-400/8 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10 max-w-3xl mx-auto text-center">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-5 text-white"
                    style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)', boxShadow: '0 4px 12px rgba(99,102,241,0.35)' }}
                  >
                    <Rocket size={12} /> More Than a Learning Platform
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-4 leading-tight tracking-tight">
                    An{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                      AI-Powered Career Acceleration Ecosystem
                    </span>
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    The Skill Genie is not just another educational platform — it is an{' '}
                    <strong className="text-gray-800">AI-powered career acceleration ecosystem</strong> that combines structured learning, practical implementation, intelligent automation, portfolio development, interview preparation, and placement readiness into one unified experience.
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    By focusing on industry-relevant technologies and real-world applications, students graduate with stronger resumes, demonstrable skills, and greater confidence to succeed in modern technology careers.
                  </p>
                </div>
              </div>

            </div>

            {/* ── CTA ─────────────────────────────────────────────── */}
            <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 text-center">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-3 leading-tight">
                  Prepare Students for{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                    Tomorrow's Technology Landscape
                  </span>
                </h2>
                <p className="text-gray-500 text-sm max-w-xl mx-auto mb-8 leading-relaxed">
                  Partner with The Skill Genie to provide students with exposure to emerging AI, automation, enterprise,
                  and low-code technologies through practical, real-world learning experiences that help them stand apart
                  in an increasingly competitive job market.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <button
                    onClick={handleGoToDashboard}
                    className="inline-flex items-center gap-2 font-black px-8 py-3.5 rounded-xl text-white text-sm transition-all active:scale-95 shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #4F46E5, #7C3AED)' }}
                  >
                    Explore The Ecosystem <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>

          </section>
          {/* ══════════════════════════════════════════════════════
              End Future Technologies Section
          ══════════════════════════════════════════════════════ */}

          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 space-y-20">

            {/* ── Institution Performance Overview ──────────────── */}
            <section>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-indigo-50 text-indigo-600 border border-indigo-100">
                  <BarChart3 size={11} /> Performance Overview
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-2">
                  Institution Performance Overview
                </h2>
                <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
                  Monitor student engagement, learning progress, technology adoption, certifications, interview readiness,
                  coding performance, and placement preparation from one centralized AI-powered dashboard.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {PERFORMANCE_CARDS.map((card) => (
                  <div
                    key={card.title}
                    className={`relative rounded-2xl border ${card.border} bg-gradient-to-br ${card.bg} p-6 overflow-hidden group hover:-translate-y-1 transition-all duration-300 hover:shadow-xl`}
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-30 -mr-4 -mt-4 pointer-events-none bg-gradient-to-br from-white to-transparent" />
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white mb-4 bg-gradient-to-br ${card.gradient} shadow-lg`}>
                      {card.icon}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 block">{card.tag}</span>
                    <h3 className="font-black text-gray-900 text-sm mb-2 leading-snug">{card.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{card.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Student Capabilities ──────────────────────────── */}
            <section id="capabilities">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-purple-50 text-purple-600 border border-purple-100">
                  <Sparkles size={11} /> Student Access
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-2">
                  Everything Your Students Can Access
                </h2>
                <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
                  Provide your students with a comprehensive AI-powered learning ecosystem designed to maximize
                  employability and career success.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {STUDENT_FEATURES.map((f) => (
                  <div
                    key={f.title}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                  >
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: f.bg, color: f.color }}
                    >
                      {f.icon}
                    </div>
                    <p className="text-sm font-bold text-gray-800 leading-snug">{f.title}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Dashboard Capabilities ───────────────────────── */}
            <section>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-emerald-50 text-emerald-600 border border-emerald-100">
                  <Building2 size={11} /> Admin Dashboard
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-2">
                  What You Can Do From Your Dashboard
                </h2>
                <p className="text-gray-500 text-sm max-w-xl mx-auto">
                  Everything you need to manage, monitor, and grow your institution's student performance in one place.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {visibleDashboardActions.map((action) => (
                  <div
                    key={action.title}
                    className="flex flex-col items-center text-center p-5 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group cursor-default"
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm"
                      style={{ background: action.bg, color: action.color }}
                    >
                      {action.icon}
                    </div>
                    <p className="text-xs font-black text-gray-800 leading-snug mb-1">{action.title}</p>
                    <p className="text-[10px] text-gray-400 leading-tight">{action.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Strategic Value ───────────────────────────────── */}
            <section>
              <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm bg-white">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-10 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-white/15 border border-white/25 text-white/90">
                      🏛 AI-POWERED INSTITUTIONAL TRANSFORMATION
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
                      Transforming Campuses Through{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-200 to-amber-200">
                        Intelligent Skill Intelligence
                      </span>
                    </h2>
                    <p className="text-indigo-200 text-sm mt-2 max-w-xl">
                      Empower your institution with AI-driven insights, measurable learning outcomes, placement intelligence, and data-backed decision-making to build a future-ready campus ecosystem.
                    </p>
                  </div>
                </div>
                <div className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {STRATEGIC_VALUES.map((point, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:bg-indigo-50 hover:border-indigo-100 transition-colors"
                    >
                      <CheckCircle2 size={15} className="text-indigo-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed font-medium">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Trust Section ─────────────────────────────────── */}
            <section>
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 bg-blue-50 text-blue-600 border border-blue-100">
                  <ShieldCheck size={11} /> Enterprise Trust
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                  Built for Modern Educational Institutions
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {TRUST_BADGES.map(({ icon, label }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center text-center gap-2 p-4 rounded-2xl border border-gray-100 bg-white hover:shadow-md hover:border-blue-100 hover:bg-blue-50 transition-all group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                      {icon}
                    </div>
                    <p className="text-[11px] font-bold text-gray-700 leading-tight">{label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Final CTA ─────────────────────────────────────── */}
            <section>
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 px-8 py-14 text-center">
                {/* Decorative blobs */}
                <div className="absolute top-0 left-0 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none -translate-x-1/3 -translate-y-1/3" />
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3" />

                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5 border border-white/20 bg-white/10 text-white/90 backdrop-blur-sm">
                    <Sparkles size={12} /> Join the Platform
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-3 leading-tight">
                    Empower Your Students.<br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
                      Strengthen Your Placements.
                    </span>
                  </h2>
                  <p className="text-indigo-200 text-sm max-w-xl mx-auto mb-8 leading-relaxed">
                    Join leading institutions leveraging AI-powered intelligence to transform student development,
                    monitor learning outcomes, and improve placement success through data-driven insights.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <button
                      onClick={handleGoToDashboard}
                      className="inline-flex items-center gap-2 bg-white text-indigo-700 font-black px-8 py-3.5 rounded-xl hover:bg-indigo-50 transition-all active:scale-95 shadow-2xl shadow-black/30 text-sm"
                    >
                      Open Dashboard <ArrowRight size={16} />
                    </button>
                    <a
                      href="#capabilities"
                      className="inline-flex items-center gap-2 bg-white/10 border border-white/25 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-white/20 transition-all text-sm backdrop-blur-sm"
                    >
                      Explore Platform <ChevronRight size={16} />
                    </a>
                  </div>
                  <p className="text-xs text-white/40 mt-8 italic">
                    The Skill Genie — Practice. Build. Become Industry-Ready.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </main>
    </div>
  );
};

export default CollegeInfoPage;
